import styled from "styled-components";
import { divider, primary, secondary } from "../../theme";

const MOBILE_MAX = "480px";
const DESKTOP_MIN = "481px";

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
  min-height: calc(100 * var(--vh));
  box-sizing: border-box;
  background-color: ${primary.light};
  padding: 24px 16px;
  padding-top: max(24px, env(safe-area-inset-top, 0px));
  padding-bottom: max(24px, env(safe-area-inset-bottom, 0px));

  @media (max-width: ${MOBILE_MAX}) {
    padding: 0;
  }
`;

export const PageCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  background-color: #fff;
  color: #212529;
  font-size: 13px;
  overflow: hidden;
`;

export const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  padding: 28px 24px 24px;
  padding-top: max(28px, env(safe-area-inset-top, 0px));
  border-bottom: 1px solid ${divider.light};
  flex-shrink: 0;

  @media (min-width: ${DESKTOP_MIN}) {
    padding: 40px 32px 28px;
    padding-top: 40px;
  }

  @media (max-width: ${MOBILE_MAX}) {
    padding: 24px 20px 20px;
    padding-top: max(24px, env(safe-area-inset-top, 0px));
    gap: 10px;
  }
`;

export const PageBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-height: 0;
  padding: 20px 24px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: ${DESKTOP_MIN}) {
    padding: 24px 32px;
  }

  @media (max-width: ${MOBILE_MAX}) {
    padding: 16px 20px;
  }
`;

export const PageFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 24px;
  padding-bottom: max(16px, env(safe-area-inset-bottom, 0px));
  border-top: 1px solid ${divider.light};
  flex-shrink: 0;
  background-color: inherit;

  @media (min-width: ${DESKTOP_MIN}) {
    padding: 20px 32px 28px;
  }

  @media (max-width: ${MOBILE_MAX}) {
    padding: 16px 20px;
    padding-bottom: max(16px, env(safe-area-inset-bottom, 0px));
  }
`;

export const PageTitle = styled.h1`
  font-weight: 600;
  font-size: clamp(18px, 4.5vw, 22px);
  line-height: 1.35;
  margin: 0;
  color: inherit;
`;

export const PageDescription = styled.p`
  font-size: clamp(13px, 3.2vw, 15px);
  line-height: 1.6;
  margin: 0;
  max-width: 100%;
  color: inherit;
  opacity: 0.85;
  text-align: center;
`;

export const PageLogo = styled.img`
  object-fit: contain;
  width: auto;
  height: clamp(72px, 16vw, 96px);
  max-width: 180px;
`;

export const PageForm = styled.form`
  flex: 1;
  min-height: 0;
  padding: 20px 24px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: ${DESKTOP_MIN}) {
    padding: 24px 32px;
  }

  @media (max-width: ${MOBILE_MAX}) {
    padding: 16px 20px;
  }
`;

export const PageTerms = styled.div`
  text-align: center;
  font-size: 12px;
  line-height: 1.55;
  color: inherit;
  opacity: 0.75;
`;

export const LinkHighlight = styled.span`
  color: ${secondary.light};
  font-weight: 500;
`;

export const ExternalLink = styled.a`
  text-decoration: none;
  color: inherit;

  &:hover ${LinkHighlight},
  &:focus-visible ${LinkHighlight} {
    text-decoration: underline;
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  @media (min-width: 540px) {
    flex-direction: row;
  }
`;

export const PageParagraph = styled.p`
  font-size: clamp(13px, 3.2vw, 15px);
  line-height: 1.6;
  margin: 0;
  color: inherit;
  opacity: 0.85;
  text-align: center;
`;

export const ActionButton = styled.button<{ $variant?: "primary" | "danger" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 48px;
  padding: 12px 16px;
  background-color: ${({ $variant }) =>
    $variant === "danger" ? "#e53935" : secondary.light};
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: clamp(13px, 2.5vw, 15px);
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(118, 53, 220, 0.35);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${secondary.light};
    outline-offset: 2px;
  }
`;

export const ActionLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex: 1;
  min-height: 48px;
  padding: 12px 16px;
  background-color: ${secondary.light};
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: clamp(13px, 2.5vw, 15px);
  font-weight: 600;
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(118, 53, 220, 0.35);
  }

  &:focus-visible {
    outline: 2px solid ${secondary.light};
    outline-offset: 2px;
  }
`;

export const ActionIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
  flex-shrink: 0;
`;

export const ActionLabel = styled.span`
  line-height: 1.3;
`;
